
/* ==========================================
   TIMI PERSONALIZER LABELS v1.4
   Final consolidated helper — TESTv2

   Supports:
   - Standard Make(r) Your Own
   - Apparel
   - Bag charms and tags
   - Desktop and mobile

   Responsibilities:
   - Consistent Edit Your Text label
   - Clean design-guide copy
   - Preserve selected-icons icon limits
   - Avoid duplicate instructions

   Does NOT:
   - Change Zepto values or pricing
   - Modify selected icon fields
   - Resize designs
   - Rebuild the icon tray

   Replaces the need for:
   timi-personalizer-mobile-refinements.js
   ========================================== */

(() => {
  'use strict';

  if (window.__timiPersonalizerLabelsV14) return;
  window.__timiPersonalizerLabelsV14 = true;

  const SCOPE = '.product-personalizer';
  const EDIT_LABEL = '✎ Edit Your Text';

  let scheduled = false;

  /* ========================================
     EDIT TEXT LABEL
     ======================================== */

  function refineEditButton(button) {
    if (!button) return;

    if (button.textContent.trim() === EDIT_LABEL) {
      return;
    }

    /*
      This button is created by TIMI's own
      selected-icons script.

      Updating its label does not affect
      the native Zepto text field or pricing.
    */

    const textNodes = [
      ...button.childNodes
    ].filter(node =>
      node.nodeType === Node.TEXT_NODE
    );

    if (
      textNodes.length === 1 &&
      button.children.length === 0
    ) {
      textNodes[0].nodeValue = EDIT_LABEL;
      return;
    }

    const walker = document.createTreeWalker(
      button,
      NodeFilter.SHOW_TEXT
    );

    let node;

    while ((node = walker.nextNode())) {
      if (/edit\s+(your\s+)?text/i.test(node.nodeValue)) {
        node.nodeValue = EDIT_LABEL;
        return;
      }
    }
  }

  /* ========================================
     ICON LIMIT NOTE
     ======================================== */

  function refineIconTray(tray) {
    const grid = tray.querySelector(
      '.timi-your-icons-grid'
    );

    if (!grid) return;

    const edit = tray.querySelector(
      '.timi-edit-text'
    );

    if (edit) {
      refineEditButton(edit);
    }

    let note = tray.querySelector(
      '.timi-icon-limit-note'
    );

    if (!note) {
      note = document.createElement('p');
      note.className = 'timi-icon-limit-note';

      grid.before(note);
    }

    /*
      The selected-icons script is the
      authority for icon availability.

      Its current version sets:
      "Add up to 1/2/3/4/5 icons"

      Do not overwrite that with a
      hardcoded number.
    */

    const countLabel = tray.querySelector(
      '.timi-your-icons-count'
    )?.textContent || '';

    const match = countLabel.match(
      /\bof\s+([1-5])\s+selected\b/i
    );

    if (match) {
      const limit = Number(match[1]);

      const expected =
        `Add up to ${limit} icons`;

      if (note.textContent.trim() !== expected) {
        note.textContent = expected;
      }
    }

    /*
      Only show the helper when the
      Edit Your Text control is available.

      This corresponds to Text + Icons
      in the TIMI selected-icons tray.
    */

    const shouldShow = Boolean(
      edit &&
      !edit.hidden &&
      !tray.hidden &&
      getComputedStyle(tray).display !== 'none'
    );

    if (note.hidden === shouldShow) {
      note.hidden = !shouldShow;
    }
  }

  /* ========================================
     DESIGN GUIDE TEXT
     ======================================== */

  function refineGuide(guide) {
    const description = guide.querySelector(
      '.timi-design-guide-heading p'
    );

    if (
      description &&
      /customize your design directly on/i.test(
        description.textContent
      ) &&
      description.textContent.trim() !== ''
    ) {
      /*
        The shorter TIMI presentation
        already explains drag and reset
        with its individual instructions.
      */
      description.textContent = '';
    }

    const reset = guide.querySelector(
      '.timi-guide-reset'
    );

    if (reset) {
      const walker = document.createTreeWalker(
        reset,
        NodeFilter.SHOW_TEXT
      );

      let node;

      while ((node = walker.nextNode())) {
        const old = node.nodeValue;

        const revised = old.replace(
          /circular arrow to reset/gi,
          'Tap reset to start over'
        );

        if (revised !== old) {
          node.nodeValue = revised;
        }
      }
    }
  }

  /* ========================================
     UPDATE
     ======================================== */

  function update() {
    document.querySelectorAll(
      `${SCOPE} .timi-your-icons`
    ).forEach(refineIconTray);

    document.querySelectorAll(
      `${SCOPE} .timi-design-guide`
    ).forEach(refineGuide);
  }

  function queueUpdate() {
    if (scheduled) return;

    scheduled = true;

    requestAnimationFrame(() => {
      scheduled = false;
      update();
    });
  }

  /* ========================================
     INITIALIZATION
     ======================================== */

  function start() {
    update();

    const observer = new MutationObserver(
      records => {
        const relevant = records.some(record => {
          const target = record.target.nodeType === 1
            ? record.target
            : record.target.parentElement;

          if (
            !target ||
            !target.closest(SCOPE)
          ) {
            return false;
          }

          const related = target.closest(
            '.timi-your-icons, .timi-design-guide'
          );

          if (related) {
            return (
              record.type === 'childList' ||
              (
                record.type === 'attributes' &&
                record.attributeName === 'hidden'
              )
            );
          }

          if (record.type !== 'childList') {
            return false;
          }

          return [...record.addedNodes].some(
            node =>
              node.nodeType === 1 &&
              (
                node.matches?.(
                  '.timi-your-icons, .timi-design-guide'
                ) ||
                node.querySelector?.(
                  '.timi-your-icons, .timi-design-guide'
                )
              )
          );
        });

        if (relevant) {
          queueUpdate();
        }
      }
    );

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });

    window.addEventListener(
      'pageshow',
      update
    );

    setTimeout(update, 650);
    setTimeout(update, 1600);

    console.log(
      'TIMI Personalizer Labels v1.4 initialized'
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start,
      { once: true }
    );
  } else {
    start();
  }
})();
